import { AxiosResponse, AxiosRequestConfig, AxiosInstance } from 'axios';
import { sliderView } from '../../utils/slider';
import {
  getCookie,
  printGroupLog,
  objectToString,
  stringToObject,
  signature,
  isFormData,
} from '../../utils';
import { get } from '../../utils/data';
import { ResponseHandler, TypeInitProps, XhrConfig } from '../../types';
import {
  dispatchVerification,
  SUSPICIOUS_LOGIN_ERROR_CODE_TO_METHOD,
} from '../../utils/web-verification';

// initResponseAdapt
const handlerMap: { [key: string]: ResponseHandler } = {
  web: {},
  sso: {
    respHandler: (res: any) => res?.error_code === 0,
    successCallback: (res: any) => {
      // sso interface { error_code: 0, description: '', mobile_ticket: '' }
      // but some sso interface { data: { ... }, message: 'success', error_code: 0 }
      if (res?.message === 'success') {
        return res.data;
      }
      return res;
    },
    failCallback: (res: any) => res,
  },
  app: {},
  auth: {
    respHandler: (res: any) => {
      console.log('====== auth ======');
      return res?.status_code === 0;
    },
    successCallback: (res: any) => {
      console.log(res);
      console.log('====== auth ======');
      return res.data;
    },
    failCallback: (res: any) => res,
  },
};

export const after = (
  res: AxiosResponse,
  xhr: AxiosInstance,
  cacheRedirectDomain?: XhrConfig['cacheRedirectDomain']
) => {
  const initProps = get('initProps');
  // get sdk type
  const { account_sdk_source } = res.config.params;
  // get sdk response adaptor
  const handler = handlerMap[account_sdk_source];
  // determine whether the request is successful
  const respHandler =
    handler?.respHandler || ((resp: any) => resp?.message === 'success');
  // format success response
  const successCallback =
    handler?.successCallback || ((resp: any) => resp?.data);
  // format fail response
  const failCallback = handler?.failCallback || ((resp: any) => resp?.data);

  const url = res?.config?.url || '';
  const logid = res?.headers?.['x-tt-logid'] || '';
  const idvDecisionConf = res?.headers?.['x-tt-verify-idv-decision-conf'] || '';
  let resp: any = res?.data || {};
  const printLog = initProps?.printLog || false;

  if (typeof resp === 'string') {
    try {
      resp = JSON.parse(resp);
    } catch (error) {
      console.log(error);
    }
  }
  // success
  // if (resp && (resp.message === 'success' || resp.error_code === 0)) {
  if (respHandler(resp)) {
    if (printLog) {
      printGroupLog(`call ${url} result >>>>`, {
        '%cSUCCESS': successCallback(resp),
        '%cLogid': logid,
      });
    }
    return Promise.resolve({
      ...(successCallback(resp) || {}),
      respHeaders: res?.headers,
      tncData: resp?.tnc_data,
    });
  }

  // fail
  if (printLog) {
    printGroupLog(`call ${url} result >>>>`, {
      '%cERROR': failCallback(resp),
      '%cLogid': logid,
    });
  }

  const errorCode = resp?.data?.error_code || resp?.error_code;
  // 302.307 https://bytedance.feishu.cn/docx/doxcnkszzfr8YfPjISFRtLUsyYe?useNewLarklet=1
  if (errorCode === 4017) {
    cacheRedirectDomain && cacheRedirectDomain(resp.data.domain);

    let requestDataStr = res.config.data || '';
    // rewrite request data
    const originRequestData = stringToObject(requestDataStr);
    const newRequestData = {
      ...originRequestData,
      ...(resp.data as any).new_params,
    };

    return xhr({
      ...res.config,
      baseURL: (res.data as any).domain,
      data: newRequestData,
    });
  }

  // web verification
  if (
    Object.keys(SUSPICIOUS_LOGIN_ERROR_CODE_TO_METHOD).includes(errorCode + '')
  ) {
    return dispatchVerification({ resp, config: res?.config }).catch(e => {
      // cancel verify
      if (!e) {
        const formatResp = failCallback(resp);
        return Promise.reject({
          ...formatResp,
          log_id: logid,
          idv_decision_conf: idvDecisionConf,
        });
      } else {
        // repeat
        console.log(e);
        return Promise.reject(e);
      }
    });
  }

  // slider
  const sliderDecisionConfig =
    resp?.data?.verify_center_decision_conf ||
    resp?.data?.verify_center_secondary_decision_conf ||
    resp?.verify_center_decision_conf ||
    resp?.verify_center_secondary_decision_conf;

  // when error_code === 2046, allow users to deal with the verification themseleves
  if (
    errorCode === 2046 &&
    typeof initProps?.secondVerifyWebOptions?.onValid === 'function'
  ) {
    initProps?.secondVerifyWebOptions?.onValid();
    return Promise.reject({ description: 'SECOND_VALID' });
  }
  if (sliderDecisionConfig && !sliderView.hasShowed) {
    sliderView.hasShowed = true;
    // when error_code === 2046 && sms_code_key, it's required to bring sms_code_key in data of the next request

    const smsCodeKey = resp?.data?.sms_code_key || resp?.sms_code_key;
    const profile_key = resp?.data?.profile_key || resp?.profile_key;
    const requestData =
      errorCode === 2046 && smsCodeKey
        ? { sms_code_key: smsCodeKey }
        : profile_key
        ? { profile_key }
        : undefined; // when has third profile_key add it to data of the next request

    return Promise.resolve(
      sliderView.show({
        verifyData: sliderDecisionConfig,
        requestConfig: res?.config,
        requestData,
      })
    )
      .then(data => {
        sliderView.hasShowed = false;
        return Promise.resolve(data);
      })
      .catch(err => {
        sliderView.hasShowed = false;
        return Promise.reject(err);
      });
  } else {
    const formatResp = failCallback(resp);
    return Promise.reject({
      ...formatResp,
      log_id: logid,
      idv_decision_conf: idvDecisionConf,
    });
  }
};

export const before = async (
  config: AxiosRequestConfig,
  commonParams: object = {},
  getDynamicConfig?: XhrConfig['getDynamicConfig']
) => {
  const initProps = get('initProps');
  const printLog = initProps?.printLog || false;
  const verifyFp = await sliderView.getFp();
  const appKey = initProps?.appKey || '';

  // const { isResend = false, ...requestConfigParams } = config.params;
  const url = config?.url;
  const method = config?.method || 'get';
  const data = config?.data || {};

  let customBaseURL = '';
  let customHeaders = {};
  let customParams = {};
  if (getDynamicConfig) {
    const { baseURL, headers, params, timeout } = getDynamicConfig({
      url,
      data,
      params: commonParams,
    });
    if (timeout) {
      config.timeout = timeout;
    }
    if (baseURL) {
      customBaseURL = baseURL;
    }

    if (params) {
      customParams = params;
    }
    customHeaders = headers || {};
  } else {
    const requestSource =
      config?.data?.passport_request_source ||
      config?.params?.passport_request_source;
    if (requestSource === 'tiktok_web_verify') {
      customBaseURL = `https://${window.location.hostname}`;
    }
  }

  // csrf headers
  const csrfHeader = {
    'x-tt-passport-csrf-token':
      getCookie('passport_csrf_token') ||
      getCookie('passport_csrf_token_default') ||
      '',
  };
  const headers = Object.assign(
    {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json, text/javascript',
    },
    config.headers || {},
    csrfHeader,
    customHeaders
    // 'x-tt-passport-js-sdk-version': __VERSION__,
    // 'x-tt-passport-use-js-sdk': 1,
  );
  config.headers = headers;
  if (customBaseURL) {
    config.baseURL = customBaseURL;
  }

  // log
  if (printLog) {
    printGroupLog(`${method} ${url} >>>>`, {
      '%cdata >>>>': data,
      '%ccommon params >>>>': commonParams,
    });
  }

  // whether the request is resent due to slider verification code
  if (config.params?.isResend) {
    const { isResend, ...realParams } = config.params;
    return {
      ...config,
      params: realParams,
    };
    // return Promise.resolve(requestConfig);
  }

  // filter data, only get defined fields
  const filterData: { [key: string]: any; [index: number]: any } = {};
  Object.keys(data)
    .filter(key => data[key] !== undefined)
    .forEach(key => {
      filterData[key] = data[key];
    });

  // data transform
  if (['post', 'POST'].includes(method)) {
    const postParams = {
      ...commonParams,
      verifyFp,
      ...customParams,
      ...(config?.params || {}),
    };
    const signParams = signature(postParams, filterData, appKey);
    config.params = { ...postParams, ...signParams };
    if (isFormData(data)) {
      config.data = data;
    } else {
      config.data = objectToString(filterData);
    }
  }

  if (['get', 'GET'].includes(method)) {
    const getParams = {
      ...filterData,
      ...commonParams,
      verifyFp,
      ...customParams,
      ...config.params,
    };
    const signParams = signature(getParams, {}, appKey);

    config.params = { ...getParams, ...signParams };
  }
  return config;
  // return Promise.resolve(requestConfig);
};
